import { useEditor, useNode } from "@craftjs/core";
import { cn } from "@/lib/utils";
import { resolveHeadingId } from "@/lib/scroll-to-anchor";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { TextBlockSettings } from "@/components/craft/settings/TextBlockSettings";

type TextBlockProps = {
  text: string;
  variant?: "p" | "h2" | "h3" | "ul";
  className?: string;
  anchorId?: string;
  style?: ResponsiveStyleProps;
};

export function TextBlock({
  text,
  variant = "p",
  className = "",
  anchorId = "",
  style = defaultStyleProps(),
}: TextBlockProps) {
  const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));

  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  const baseClass = cn(
    variant === "h2" && "mt-12 scroll-mt-28 text-2xl font-bold tracking-tight text-primary md:text-3xl",
    variant === "h3" && "mt-8 scroll-mt-28 text-xl font-bold tracking-tight text-primary",
    variant === "ul" && "mt-5 whitespace-pre-line text-lg leading-relaxed text-foreground/85",
    variant === "p" && "mt-5 text-lg leading-relaxed text-foreground/85 md:text-[17px]",
    className,
    styleClass,
    selected && enabled && "outline outline-2 outline-dashed outline-primary/40",
  );

  const headingId =
    (variant === "h2" || variant === "h3") && text.trim()
      ? resolveHeadingId(text, anchorId)
      : undefined;

  if (!enabled) {
    const Tag = variant === "h2" ? "h2" : variant === "h3" ? "h3" : "p";
    return (
      <Tag id={headingId} className={baseClass} style={inlineStyle}>
        {text}
      </Tag>
    );
  }

  const shared = {
    ref: (ref: HTMLElement | null) => {
      if (ref) connect(drag(ref));
    },
    id: headingId,
    contentEditable: true as const,
    suppressContentEditableWarning: true,
    onBlur: (e: React.FocusEvent<HTMLElement>) =>
      setProp((p: TextBlockProps) => {
        p.text = e.currentTarget.innerText;
      }),
    onInput: (e: React.FormEvent<HTMLElement>) =>
      setProp((p: TextBlockProps) => {
        p.text = e.currentTarget.innerText;
      }),
    className: baseClass,
    style: inlineStyle,
  };

  if (variant === "h2") return <h2 {...shared}>{text}</h2>;
  if (variant === "h3") return <h3 {...shared}>{text}</h3>;
  return <p {...shared}>{text}</p>;
}

TextBlock.craft = {
  displayName: "Text",
  props: {
    text: "Enter text…",
    variant: "p",
    className: "",
    anchorId: "",
    style: defaultStyleProps(),
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: TextBlockSettings,
  },
};
